export type HttpVerb = 'GET' | 'POST';

export type HttpParamSource =
	| { readonly kind: 'arg'; readonly name?: string }
	| { readonly kind: 'body' }
	| { readonly kind: 'userId' }
	| { readonly kind: 'userIdOptional' };

export interface HttpMethodMeta {
	readonly verb: HttpVerb;
	readonly path: string;
	readonly authRequired?: boolean;
}

export interface HttpServiceMeta {
	readonly basePath?: string;
	readonly authRequired?: boolean;
	readonly methods: Readonly<Record<string, HttpMethodMeta>>;
	readonly params: Readonly<Record<string, Readonly<Record<number, HttpParamSource>>>>;
}

type MutableHttpServiceMeta = {
	basePath?: string;
	authRequired?: boolean;
	methods: Record<string, HttpMethodMeta>;
	params: Record<string, Record<number, HttpParamSource>>;
};

const HTTP_META_KEY = Symbol.for('lingvo-news:http:meta');

function getOwnMeta(ctor: Function): MutableHttpServiceMeta | undefined {
	const rec = ctor as unknown as Record<symbol, unknown>;
	const value = rec[HTTP_META_KEY];
	if (!value || typeof value !== 'object') return undefined;
	return value as MutableHttpServiceMeta;
}

function getOrCreateOwnMeta(ctor: Function): MutableHttpServiceMeta {
	const existing = getOwnMeta(ctor);
	if (existing) return existing;

	const created: MutableHttpServiceMeta = {
		methods: {},
		params: {},
	};
	Object.defineProperty(ctor, HTTP_META_KEY, {
		value: created,
		writable: false,
		enumerable: false,
		configurable: false,
	});
	return created;
}

function isFunction(value: unknown): value is Function {
	return typeof value === 'function';
}

export function setHttpBasePath(ctor: Function, basePath: string): void {
	getOrCreateOwnMeta(ctor).basePath = basePath;
}

export function setHttpAuthRequired(ctor: Function, authRequired: boolean): void {
	getOrCreateOwnMeta(ctor).authRequired = authRequired;
}

export function setHttpMethodMeta(ctor: Function, methodName: string, meta: HttpMethodMeta): void {
	getOrCreateOwnMeta(ctor).methods[methodName] = meta;
}

export function setHttpParamSource(ctor: Function, methodName: string, index: number, source: HttpParamSource): void {
	const meta = getOrCreateOwnMeta(ctor);
	meta.params[methodName] ??= {};
	meta.params[methodName][index] = source;
}

export function getHttpServiceMeta(ctor: Function): HttpServiceMeta {
	// Merge prototype chain from base -> derived so derived overrides base.
	const chain: Function[] = [];
	let cur: unknown = ctor;
	while (isFunction(cur)) {
		chain.unshift(cur);
		cur = Object.getPrototypeOf(cur);
	}

	const merged: MutableHttpServiceMeta = {
		methods: {},
		params: {},
	};

	for (const c of chain) {
		const own = getOwnMeta(c);
		if (!own) continue;

		if (typeof own.basePath === 'string') merged.basePath = own.basePath;
		if (typeof own.authRequired === 'boolean') merged.authRequired = own.authRequired;

		Object.assign(merged.methods, own.methods);
		for (const [methodName, paramByIndex] of Object.entries(own.params)) {
			merged.params[methodName] = { ...(merged.params[methodName] ?? {}), ...paramByIndex };
		}
	}

	return merged;
}


